import {
  DuplicationMatchResult,
  InlineComment,
  PublisherReviewScanStatus,
  WorkflowEventTypes,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import PQueue from 'p-queue';

import { IMplServiceClient } from '../api/IMplServiceClient';
import { log } from '../loggers/Logger';
import { ContentEditorService } from './ContentEditorService';
import { InlineCommentManager } from './InlineCommentManager';
import { PollingTimer } from './PollingTimer';
import { isScannerComment } from './ScannerComments';
import { normalizeSimilarityScore } from './ScannerFindings';
import { TypedEmitter, Unsubscribe } from './TypedEmitter';

/** The publisher review scan lifecycle state. */
export type PublisherReviewScanState = 'idle' | 'scan_in_progress' | 'loading_results';

/** How the publisher review scan concluded. */
export type PublisherReviewScanCompletionStatus = 'completed' | 'failed' | 'timed_out';

/** Outcome of the dedicated duplication-results fetch, kept separate so the
 * duplication row can show its error independently of the overall scan. */
export type DuplicationResultsState =
  | { readonly status: 'ok'; readonly matches: readonly DuplicationMatchResult[] }
  | { readonly status: 'failed' };

/** Results fetched after the publisher review scan concludes. */
export interface PublisherReviewResults {
  readonly status: PublisherReviewScanCompletionStatus;
  readonly scanComments: readonly InlineComment[] | undefined;
  readonly duplicationResults: DuplicationResultsState;
}

/** Events emitted by PublisherReviewService. */
export type PublisherReviewEvents = {
  changed: () => void;
};

/** Shared failed duplication-results state to avoid re-allocating the literal. */
const DUPLICATION_RESULTS_FAILED: DuplicationResultsState = { status: 'failed' };

/**
 * Timeout duration for the scan in milliseconds (5 minutes).
 *
 * Matches the quality check budget: scans have been observed finishing just
 * over 3 minutes, so a tighter limit reports a slow success as a failure.
 */
const SCAN_TIMEOUT_MS = 300_000;

/** Polling interval for checking workflow scan status (5 seconds). */
const POLL_INTERVAL_MS = 5_000;

/**
 * Manages the publisher review scan lifecycle.
 *
 * All public mutations are serialized via PQueue to prevent interleaving.
 *
 * Flow:
 * 1. Content enters PUBLISHER_REVIEW state (scan triggers automatically)
 * 2. Service polls getContentWorkflow() checking publisherReviewInfo.scanStatus
 * 3. When scanStatus === COMPLETE → fetches scan comments and duplication results
 * 4. When scanStatus === FAILED → transitions to idle with error status
 */
export class PublisherReviewService {
  private readonly _emitter = new TypedEmitter<PublisherReviewEvents>();
  private readonly _mpl: IMplServiceClient;
  private readonly _contentEditorService: ContentEditorService;
  private readonly _commentManager: InlineCommentManager;
  private readonly _queue = new PQueue({ concurrency: 1 });
  private readonly _timer = new PollingTimer(POLL_INTERVAL_MS, SCAN_TIMEOUT_MS);
  private readonly _unsubComments: Unsubscribe;
  private _state: PublisherReviewScanState = 'idle';
  private _results: PublisherReviewResults | null = null;
  /** Bumped on every reset; async work discards itself once its captured value goes stale. */
  private _generation = 0;
  private _refreshing = false;
  private _contentId: string | null = null;

  constructor(
    mpl: IMplServiceClient,
    contentEditorService: ContentEditorService,
    commentManager: InlineCommentManager,
  ) {
    this._mpl = mpl;
    this._contentEditorService = contentEditorService;
    this._commentManager = commentManager;

    this._unsubComments = commentManager.on('changed', () => this.handleExternalFindingsChange());
  }

  get state(): PublisherReviewScanState {
    return this._state;
  }

  get results(): PublisherReviewResults | null {
    return this._results;
  }

  /**
   * Load publisher review scan state for `contentId`, starting polling that
   * handles whatever state the scan is in. A no-op for content already being
   * tracked; switching content resets prior polling and results before loading.
   */
  loadResults(contentId: string): void {
    // Captured now, not when the operation dequeues: a reset while this waits in
    // the queue must discard it rather than let it adopt the new generation.
    const generation = this._generation;
    void this._queue.add(() => this.doLoadResults(contentId, generation));
  }

  /**
   * Resubmit rejected content for publisher review. Posts a
   * PUBLISHER_REVIEW_RESUBMIT workflow event then refreshes the workflow.
   *
   * @throws {ServiceCallError} when the backend call fails.
   */
  async resubmitForReview(contentId: string, workflowId: number): Promise<void> {
    await this._mpl.postWorkflowEvent(
      contentId,
      workflowId,
      WorkflowEventTypes.PUBLISHER_REVIEW_RESUBMIT,
      '',
      undefined,
      undefined,
      undefined,
      { suppressSuccessFlashbar: true },
    );
    await this._contentEditorService.refreshWorkflow();
  }

  on<K extends keyof PublisherReviewEvents>(
    event: K,
    listener: PublisherReviewEvents[K],
  ): Unsubscribe {
    return this._emitter.on(event, listener);
  }

  /** Drop all scan state and abandon in-flight polling. Call right before loading a content. */
  resetState(): void {
    this._generation++;
    this._timer.stop();
    this._state = 'idle';
    this._results = null;
    this._contentId = null;
    this._emitter.emit('changed');
  }

  /** Release all resources. Call on provider unmount. */
  close(): void {
    this._unsubComments();

    // Bumping the generation neutralizes queued and in-flight work, so the queue
    // is left alone — clearing it would leave callers awaiting `add()` forever.
    this.resetState();
  }

  private doLoadResults(contentId: string, generation: number): void {
    if (generation !== this._generation) return;

    // Already tracking this content (loaded, loading, or scanning): keep its state.
    // The service is an app-wide singleton, so this guard — not the panel — is
    // what dedupes repeated loads, including reopening the panel for the same content.
    if (contentId === this._contentId) return;

    // Switching content: drop the prior content's polling and results so a stale
    // scan from the previously open content can't bleed into this one.
    this.resetState();
    this._state = 'loading_results';
    this._contentId = contentId;
    this._emitter.emit('changed');

    // The reset above bumped the generation, so the poll cycle tracks the new one.
    const pollGeneration = this._generation;
    this._timer.start(
      () => void this._queue.add(() => this.doPoll(contentId, pollGeneration)),
      () => this.handleTimeout(pollGeneration),
    );
  }

  private async doPoll(contentId: string, generation: number): Promise<void> {
    // A poll queued for previously-open content must not run after a content switch.
    if (!this.isActive(generation)) return;

    try {
      const response = await this._mpl.getContentWorkflow(contentId);
      const workflow = response.workflow;

      // The content may have been unloaded or switched while the request was in flight.
      if (!this.isActive(generation)) return;

      if (workflow?.draftState !== WorkflowState.PUBLISHER_REVIEW) {
        this.transitionToIdle(generation, null);
        return;
      }

      const scanStatus = workflow.publisherReviewInfo?.scanStatus;

      if (scanStatus === PublisherReviewScanStatus.IN_PROGRESS) {
        if (this._state === 'loading_results') {
          this._state = 'scan_in_progress';
          this._emitter.emit('changed');
        }
      } else if (scanStatus === PublisherReviewScanStatus.COMPLETE) {
        await this.handleScanComplete(contentId, generation, workflow.publisherReviewInfo?.scanId);
        return;
      } else if (scanStatus === PublisherReviewScanStatus.FAILED) {
        this.transitionToIdle(generation, {
          status: 'failed',
          scanComments: undefined,
          duplicationResults: DUPLICATION_RESULTS_FAILED,
        });
        return;
      }
    } catch (error) {
      log.error({ error, contentId }, 'Failed to poll workflow for publisher review scan status');
    }

    if (this.isActive(generation)) {
      this._timer.scheduleNext();
    }
  }

  private async handleScanComplete(
    contentId: string,
    generation: number,
    scanId?: string,
  ): Promise<void> {
    if (!this.isActive(generation)) return;

    try {
      const [, , duplicationResults] = await Promise.all([
        this._commentManager.refresh(),
        this._contentEditorService.refreshWorkflow(),
        this.fetchDuplicationResults(contentId, scanId),
      ]);

      const allComments = this._commentManager.openedComments?.comments ?? [];
      const scanComments = allComments.filter(isScannerComment);

      this.transitionToIdle(generation, {
        status: 'completed',
        scanComments,
        duplicationResults,
      });
    } catch (error) {
      log.error({ error, contentId }, 'Failed to fetch publisher review scan results');
      this.transitionToIdle(generation, {
        status: 'failed',
        scanComments: undefined,
        duplicationResults: DUPLICATION_RESULTS_FAILED,
      });
    }
  }

  /**
   * Fetches duplication match results for the completed scan. Failure here is
   * isolated so it never flips the overall scan status — other scanner rows
   * must keep rendering their counts.
   */
  private async fetchDuplicationResults(
    contentId: string,
    scanId?: string,
  ): Promise<DuplicationResultsState> {
    // When no scanId is available, the duplication row cannot be resolved.
    if (!scanId) return DUPLICATION_RESULTS_FAILED;

    try {
      const response = await this._mpl.getDuplicationResults(contentId, scanId);
      if (response.error) return DUPLICATION_RESULTS_FAILED;

      // The endpoint may emit scores on a 0-1 scale; normalize to the 0-100
      // scale the duplication UI (threshold and badge) expects.
      const matches = (response.results ?? []).map((match) => ({
        ...match,
        similarityScore: normalizeSimilarityScore(match.similarityScore),
      }));
      return { status: 'ok', matches };
    } catch (error) {
      log.error({ error, contentId, scanId }, 'Failed to fetch duplication results');
      return DUPLICATION_RESULTS_FAILED;
    }
  }

  private handleTimeout(generation: number): void {
    if (!this.isActive(generation)) return;

    log.error({ contentId: this._contentId }, 'Publisher review scan timed out');
    this.transitionToIdle(generation, {
      status: 'timed_out',
      scanComments: undefined,
      duplicationResults: DUPLICATION_RESULTS_FAILED,
    });
  }

  private isActive(generation: number): boolean {
    return (
      generation === this._generation &&
      (this._state === 'scan_in_progress' || this._state === 'loading_results')
    );
  }

  private handleExternalFindingsChange(): void {
    if (this._refreshing || this._state !== 'idle') return;

    this._emitter.emit('changed');

    // Skip refresh when the comment manager has nothing open — the 'changed'
    // event also fires on close() during teardown (e.g. navigating away after
    // submit).
    if (this._results && this._contentId && this._commentManager.openedComments) {
      const generation = this._generation;
      void this._queue.add(() => this.doRefreshResults(generation));
    }
  }

  private async doRefreshResults(generation: number): Promise<void> {
    this._refreshing = true;
    try {
      if (generation !== this._generation) return;

      const allComments = this._commentManager.openedComments?.comments ?? [];
      const scanComments = allComments.filter(isScannerComment);

      this._results = {
        status: this._results?.status ?? 'completed',
        scanComments,
        duplicationResults: this._results?.duplicationResults ?? DUPLICATION_RESULTS_FAILED,
      };
      this._emitter.emit('changed');
    } catch (error) {
      log.error(
        { error, contentId: this._contentId },
        'Failed to refresh publisher review results',
      );
    } finally {
      this._refreshing = false;
    }
  }

  private transitionToIdle(generation: number, results: PublisherReviewResults | null): void {
    if (generation !== this._generation) return;

    this._timer.stop();
    this._state = 'idle';
    this._results = results;
    this._emitter.emit('changed');
  }
}
